import { ModalConfirm } from '@/components/element/modal/ModalConfirm';
import { ModalMessage } from '@/components/element/modal/ModalMessage';
import { MotionDiv } from '@/components/element/MotionDiv';
import { SimpleTextFormatWithLink } from '@/components/element/SimpleTextFormat';
import { useTeamPurchaseOrderPhases } from '@/hooks/api/0260_team_purchase_order_phases';
import { useDestroyPurchaseOrder } from '@/hooks/api/0550_destroy_purchase_order';
import { useArchivePurchaseOrder } from '@/hooks/api/0560_archive_purchase_order';
import { useUnarchivePurchaseOrder } from '@/hooks/api/0561_unarchive_purchase_order';
import { usePurchaseOrderInvalidate } from '@/hooks/api/0570_purchase_order';
import { useUpdatePurchaseOrderPhase } from '@/hooks/api/0580_update_purchase_order_phase';
import { PurchaseOrderLogic } from '@/hooks/api/models/logics';
import { toastApiError, toastError, toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentPurchaseOrder } from '@/providers/PurchaseOrderProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { dateFormat } from '@/utils';
import { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export const ProjectSummary = () => {
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { _po_ } = useCurrentPurchaseOrder();
  const { mutate, isPending, isSuccess, error } = useDestroyPurchaseOrder();

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (isSuccess) {
      toastSuccess('プロジェクト（費用）を削除しました');
      navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders`);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (error) {
      const msgs: string[] = (error as any).response.data.errors || [];
      if (msgs.length > 0) toastError(msgs[0]);
    }
  }, [error]);

  const onDeleteClick = () => {
    mutate({ identifier: _w_!.identifier, team_id: _t_!.id, purchase_order_id: _po_!.id });
  };

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div>
          <h6 className="mb-0">プロジェクト基本情報</h6>
        </div>
        <div className="d-flex align-items-center">
          <NewPurchaseOrderFromCopyLink />
          <Link
            to={`/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${_po_!.id}/edit`}
            className="btn btn-outline-success btn-icon-only"
          >
            <i className="fas fa-edit"></i>
          </Link>
          <div className="dropstart ms-3">
            <a href="" className="text-secondary" id="dropdownMarketingCard" data-bs-toggle="dropdown">
              <i className="fas fa-ellipsis-v"></i>
            </a>
            <ul className="dropdown-menu dropdown-menu-lg-start px-2 py-3" aria-labelledby="dropdownMarketingCard">
              <li>{_po_!.archived ? <UnarchiveProjectLink /> : <ArchiveProjectLink />}</li>
              <li>
                <button
                  data-bs-toggle="modal"
                  data-bs-target={`#delConfirm`}
                  className="dropdown-item border-radius-md text-danger"
                >
                  <i className="fas fa-trash-alt me-2"></i>プロジェクトを削除
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="card-body pt-0">
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">プロジェクト名</div>
          <div className="font-weight-bold text-end px-0 text-sm text-dark">{_po_!.name}</div>
        </div>
        <hr className="horizontal dark" />
        {_po_!.archived && (
          <>
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-secondary text-xs font-weight-bold pe-2">アーカイブ</div>
              <div className="font-weight-bold text-end px-0 text-sm text-dark">
                <span className="badge badge-danger">アーカイブ済</span>
              </div>
            </div>
            <hr className="horizontal dark" />
          </>
        )}
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">フェーズ</div>
          <div className="font-weight-bold text-end px-0 d-flex align-items-stretch">
            <PhasesSelect />
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">プロジェクト担当者</div>
          <div className="d-flex align-items-center">
            <img src={_po_!.user.user_account.icon_file_url} className="avatar avatar-xs rounded-circle me-1" />
            <div className="font-weight-bold px-0 text-sm text-dark">{_po_!.user.user_account.name}</div>
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">関連メンバー</div>
          <div className="d-flex align-items-center justify-content-center">
            <div className="avatar-group text-nowrap overflow-hidden flex-1">
              {_po_!.purchase_order_members.map((m) => (
                <img
                  key={`po-m-img-${m.id}`}
                  src={m.user.user_account.icon_file_url}
                  className="avatar avatar-xs rounded-circle"
                />
              ))}
            </div>
            {PurchaseOrderLogic.canUpdate(_t_!, _po_!, me!) && (
              <Link
                to={`/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${_po_!.id}/purchase_order_members`}
                className="btn btn-sm bg-gradient-secondary ms-2 px-3"
              >
                編集
              </Link>
            )}
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">発注先</div>
          <div className="font-weight-bold text-end px-0 text-sm text-dark">{_po_!.business_partner.display_name}</div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">支払タイプ</div>
          <div className="font-weight-bold text-end px-0 text-sm text-dark">{_po_!.payment_context.label}</div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">
            費用（税抜）
            {_po_!.accounting_manual_mode && <> - 手動管理</>}
          </div>
          <div className="d-flex align-items-center">
            <div className="font-weight-bold text-end px-0 text-sm text-dark">
              ¥{_po_!.accounting_total_amount.toLocaleString()}
            </div>
            <PaymentSchedulesLink />
          </div>
        </div>
        {_po_!.payment_context.name == 'spot' ? (
          <>
            <hr className="horizontal dark" />
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-secondary text-xs font-weight-bold pe-2">請求日</div>
              <div className="font-weight-bold text-end px-0 text-sm text-dark">
                {dateFormat({ date: _po_!.spot_billing_date! })}
              </div>
            </div>
            <hr className="horizontal dark" />
          </>
        ) : (
          <>
            <hr className="horizontal dark" />
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-secondary text-xs font-weight-bold pe-2">契約期間</div>
              <div className="font-weight-bold text-end px-0 text-sm text-dark">
                {_po_!.contract_start_date ? dateFormat({ date: _po_!.contract_start_date }) : ''} -{' '}
                {_po_!.contract_end_date ? dateFormat({ date: _po_!.contract_end_date }) : ''}
              </div>
            </div>
            <hr className="horizontal dark" />
          </>
        )}
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">支払方法</div>
          <div className="font-weight-bold text-end px-0 text-sm text-dark">{_po_!.payment_method.label}</div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">勘定項目</div>
          <div className="font-weight-bold text-end px-0 text-sm text-dark">
            {_po_!.expense_account_items.map((eai, idx) => (
              <span key={`eai-${eai.id}`}>
                {idx != 0 && <br />}
                {eai.label}
              </span>
            ))}
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">メモ</div>
          <div className="px-0 text-xs break-word">
            <SimpleTextFormatWithLink content={_po_!.description || ''} />
          </div>
        </div>
        <hr className="horizontal dark" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-secondary text-xs font-weight-bold pe-2">支払タスク処理マニュアル</div>
          <div className="px-0 text-xs break-word">
            <SimpleTextFormatWithLink content={_po_!.task_procedure_manual || ''} />
          </div>
        </div>
      </div>

      <ModalConfirm modalId={'delConfirm'} okClicked={onDeleteClick} message={'削除します。よろしいですか？'} />
    </MotionDiv>
  );
};

// コピーして作成ボタン
const NewPurchaseOrderFromCopyLink = () => {
  const navigate = useNavigate();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _po_ } = useCurrentPurchaseOrder();

  const handleOkClick = () => {
    navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/new`, { state: { copyFrom: _po_! } });
  };

  return (
    <>
      <ModalConfirm
        modalId={'copyPurchaseOrderConfirm'}
        okClicked={handleOkClick}
        message={
          '既存のプロジェクト情報をコピーして\n新たなプロジェクトを作成します。\n\n期間や金額の設定内容に注意してください。'
        }
      />

      <button
        className="btn btn-outline-info btn-icon-only me-1"
        data-bs-toggle="modal"
        data-bs-target={`#copyPurchaseOrderConfirm`}
      >
        <i className="fas fa-copy"></i>
      </button>
    </>
  );
};

// アーカイブボタン
const ArchiveProjectLink = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _po_ } = useCurrentPurchaseOrder();
  const { mutate, isPending, isSuccess, error } = useArchivePurchaseOrder();
  const { invalidate } = usePurchaseOrderInvalidate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _po_!.id });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess) {
      toastSuccess('アーカイブしました');
      invalidate();
    }
  }, [isSuccess]);

  const onArchiveClick = () => {
    mutate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _po_!.id });
  };

  return (
    <button className="dropdown-item border-radius-md" onClick={onArchiveClick}>
      <i className="fas fa-archive me-2"></i>プロジェクトをアーカイブ
    </button>
  );
};

// アーカイブ解除ボタン
const UnarchiveProjectLink = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _po_ } = useCurrentPurchaseOrder();
  const { mutate, isPending, isSuccess, error } = useUnarchivePurchaseOrder();
  const { invalidate } = usePurchaseOrderInvalidate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _po_!.id });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess) {
      toastSuccess('アーカイブを解除しました');
      invalidate();
    }
  }, [isSuccess]);

  const onArchiveClick = () => {
    mutate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _po_!.id });
  };

  return (
    <button className="dropdown-item border-radius-md" onClick={onArchiveClick}>
      <i className="fas fa-box-open me-2"></i>アーカイブを解除
    </button>
  );
};

// フェーズ
const PhasesSelect = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { _po_, mePurchaseOrderMember } = useCurrentPurchaseOrder();
  const { invalidate } = usePurchaseOrderInvalidate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _po_!.id });
  const { data: phases } = useTeamPurchaseOrderPhases({ identifier: _w_!.identifier, team_id: _t_!.id });
  const { mutate, data, error } = useUpdatePurchaseOrderPhase();
  const showMessageButtonRef = useRef<HTMLButtonElement>(null);

  const onPhaseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setShowLoading(true);
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: _po_!.id,
      team_purchase_order_phase_id: Number(e.target.value),
    });
  };

  useEffect(() => {
    if (data) {
      invalidate();
      setShowLoading(false);

      if ('契約未承認' == data.alert) {
        // フェーズ移動でアラートが設定された場合にモーダルでメッセージ表示
        showMessageButtonRef.current?.click();
      }
    }
  }, [data]);

  return (
    <>
      <ModalMessage
        modalId="messageModal"
        message="承認された契約タスクがありません"
        showModalButtonRef={showMessageButtonRef}
      />
      <select
        className="form-control form-control-sm"
        value={_po_!.team_purchase_order_phase.id}
        onChange={(e) => onPhaseChange(e)}
      >
        {phases?.map((phase) => (
          <option key={`po-phase-${phase.id}`} value={phase.id}>
            {phase.label}
          </option>
        ))}
      </select>
    </>
  );
};

// 支払時期ボタン
const PaymentSchedulesLink = () => {
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _po_ } = useCurrentPurchaseOrder();

  return (
    <Link
      to={`/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${_po_!.id}/payment_schedules`}
      className="btn btn-sm bg-gradient-secondary ms-2 px-3"
    >
      詳細
    </Link>
  );
};
